import { getLanguageCode } from 'locales';
import { 
  UploadDynamicInventoryPolicy, 
  UploadDynamicInventoryPolicyEnCSVEmpty, 
  UploadDynamicInventoryPolicyEsCSVEmpty, 
  UploadDynamicInventoryPolicyPtBrCSVEmpty,
  UploadDynamicInventoryPolicyRequiredFieldsEn,
  UploadDynamicInventoryPolicyRequiredFieldsEs,
  UploadDynamicInventoryPolicyRequiredFieldsPtBr
} from './dynamic-inventory-policy.types';
import intl from 'react-intl-universal';
import { parseNumberByLocaleWithGroup } from 'locales/utils';
import { DynamicInventoryPolicyProcessor } from './dynamic-inventory-policy.processor';

export const createDynamicInventoryPolicyPromises = async (
  fileData: UploadDynamicInventoryPolicy[],
  treatSuccess: (data: any) => void,
  treatError: () => void,
  setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>
) => {
  try {
    const processor = new DynamicInventoryPolicyProcessor(setProcessingPercentual);
    const summary = await processor.processItems(fileData);
    const response = processor.buildResponse(summary);
    treatSuccess(response);
  } catch (e) {
    console.error('Error processing dynamic inventory policy:', e);
    treatError();
  }
};

export const validateDynamicInventoryPolicyCsvEmpty = () => {
  const languageCode = getLanguageCode();
  
  if (languageCode === "pt") {
    return UploadDynamicInventoryPolicyPtBrCSVEmpty;
  }
  
  if (languageCode === "es") {
    return UploadDynamicInventoryPolicyEsCSVEmpty;
  }
  
  return UploadDynamicInventoryPolicyEnCSVEmpty;
};

export const validateDynamicInventoryPolicyRequiredFields = () => {
  const languageCode = getLanguageCode();
  
  if (languageCode === "pt") {
    return UploadDynamicInventoryPolicyRequiredFieldsPtBr;
  }
  
  if (languageCode === "es") {
    return UploadDynamicInventoryPolicyRequiredFieldsEs;
  }
  
  return UploadDynamicInventoryPolicyRequiredFieldsEn;
};

export const validateDynamicInventoryPolicyDelimiter = () => {
  const languageCode = getLanguageCode();
  
  if (languageCode === "en") {
    return ",";
  }
  
  return ";";
};

export const formatAndValidateDynamicInventoryPolicyNumber = (strNumber: string, validations: string[], field: string) => {
  try {
    if (!strNumber || strNumber.trim() === '') {
      return null; // Campos numéricos podem ser opcionais
    }

    const normalizedNumber = parseNumberByLocaleWithGroup(strNumber);

    if (normalizedNumber === null || normalizedNumber === undefined)
      throw new Error();

    const _number = Number(normalizedNumber);

    if (isNaN(_number))
      throw new Error()

    return _number;

  } catch (e) {
    validations.push(field);
    console.error(e);
    return null;
  }
}

interface IValidationsDynamicInventoryPolicyResult {
  isValid: boolean,
  validationMessage: string[]
  data: UploadDynamicInventoryPolicy[],
}

const getMissingFields = (data: string[]) => (`${data.join(', ')}.`);

export const getFieldNamesByLanguage = () => {
  const languageCode = getLanguageCode();
  
  let minStockField = 'Minimum Stock';
  let maxStockField = 'Maximum Stock';
  let replenishmentField = 'Supply Point';
  let plantField = 'Plant';
  let startDateField = 'Start Date';
  let endDateField = 'End Date';
  
  if (languageCode === 'pt') {
    minStockField = 'Estoque Mínimo';
    maxStockField = 'Estoque Máximo';
    replenishmentField = 'Ponto Reabastecimento';
    plantField = 'Planta';
    startDateField = 'Data Início';
    endDateField = 'Data Término';
  } else if (languageCode === 'es') {
    minStockField = 'Stock Mínimo';
    maxStockField = 'Stock Máximo';
    replenishmentField = 'Punto de Reabastecimiento';
    plantField = 'Planta';
    startDateField = 'Fecha de Inicio';
    endDateField = 'Fecha de término';
  }

  return { minStockField, maxStockField, replenishmentField, plantField, startDateField, endDateField };
};

export const validateItemRequiredFields = (item: UploadDynamicInventoryPolicy, line: number, result: IValidationsDynamicInventoryPolicyResult): boolean => {
  const requiredFields = validateDynamicInventoryPolicyRequiredFields();
  const dataMissing = requiredFields?.filter((field) => {
    const value = item[field];
    return !value || value.trim() === ''
  });

  if (dataMissing.length !== 0) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('master-data.general-configuration.missingRequiredFields')} ${line}: ${getMissingFields(dataMissing)}`);
    return false;
  }

  return true;
};

export const validateItemNumericFields = (item: UploadDynamicInventoryPolicy, line: number, result: IValidationsDynamicInventoryPolicyResult): boolean => {
  const lineValidations: string[] = [];
  const { minStockField, maxStockField, replenishmentField } = getFieldNamesByLanguage();

  if (item[minStockField] && item[minStockField].trim() !== '') {
    formatAndValidateDynamicInventoryPolicyNumber(item[minStockField], lineValidations, minStockField);
  }
  if (item[maxStockField] && item[maxStockField].trim() !== '') {
    formatAndValidateDynamicInventoryPolicyNumber(item[maxStockField], lineValidations, maxStockField);
  }
  if (item[replenishmentField] && item[replenishmentField].trim() !== '') {
    formatAndValidateDynamicInventoryPolicyNumber(item[replenishmentField], lineValidations, replenishmentField);
  }

  if (lineValidations.length) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('commons.line')} ${line} - ${intl.get('master-data.general-configuration.fieldErrors')} ${lineValidations.join(', ')}`);
    return false;
  }

  return true;
};

export const processValidationItem = (item: UploadDynamicInventoryPolicy, line: number, result: IValidationsDynamicInventoryPolicyResult): void => {
  try {
    const isRequiredFieldsValid = validateItemRequiredFields(item, line, result);
    if (!isRequiredFieldsValid) {
      return;
    }

    const isNumericFieldsValid = validateItemNumericFields(item, line, result);
    if (!isNumericFieldsValid) {
      return;
    }

    result.data.push(item);
  } catch (e) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('master-data.general-configuration.errorLine')} ${line}`);
    console.error(e);
  }
};

export const filterEmptyItems = (items: UploadDynamicInventoryPolicy[]): UploadDynamicInventoryPolicy[] => {
  const _items = items[0] as unknown as UploadDynamicInventoryPolicy[];
  return _items?.filter(x => x && Object.values(x).some(value => value && value.toString().trim() !== '')) || [];
};

export const validateFileHeaders = (items: UploadDynamicInventoryPolicy[]): boolean => {
  const UploadDynamicInventoryPolicyCSVEmpty = validateDynamicInventoryPolicyCsvEmpty();
  return Object.keys(UploadDynamicInventoryPolicyCSVEmpty).every(x => Object.keys(items[0]).includes(x));
};

const validateFileStructure = (items: UploadDynamicInventoryPolicy[]): { isValid: boolean; filteredItems: UploadDynamicInventoryPolicy[]; errorMessage?: string } => {
  const filteredItems = filterEmptyItems(items);

  if (!filteredItems.length) {
    return {
      isValid: false,
      filteredItems: [],
      errorMessage: intl.get('master-data.general-configuration.invalidFile')
    };
  }

  if (!validateFileHeaders(filteredItems)) {
    return {
      isValid: false,
      filteredItems: [],
      errorMessage: intl.get('master-data.general-configuration.incorrectHeader')
    };
  }

  return {
    isValid: true,
    filteredItems
  };
};

const processFileItems = (items: UploadDynamicInventoryPolicy[], result: IValidationsDynamicInventoryPolicyResult): void => {
  let line = 2;
  for (const item of items) {
    processValidationItem(item, line, result);
    line++;
  }
};

export const formatAndValidateDynamicInventoryPolicyFile: (items: UploadDynamicInventoryPolicy[]) => IValidationsDynamicInventoryPolicyResult = (items: UploadDynamicInventoryPolicy[]) => {
  const _result: IValidationsDynamicInventoryPolicyResult = {
    data: [],
    isValid: true,
    validationMessage: []
  };

  const fileValidation = validateFileStructure(items);
  if (!fileValidation.isValid) {
    _result.isValid = false;
    _result.validationMessage.push(fileValidation.errorMessage!);
    return _result;
  }

  processFileItems(fileValidation.filteredItems, _result);
  return _result;
};
